import { ROUTES } from '@/shared/constants/routes'

/** Identificadores alineados con el JSON en `system.modules` (backend). */
export type SystemModuleId =
  | 'students'
  | 'teachers'
  | 'tutors'
  | 'courses'
  | 'periods'
  | 'enrollments'
  | 'grades'
  | 'attendance'
  | 'classrooms'
  | 'logistics'
  | 'followUp'
  | 'users'
  | 'notifications'
  | 'initialSetup'
  | 'studyPlans'
  | 'gradeScales'
  | 'actaBooks'
  | 'elBicho'

export const SYSTEM_MODULES_SETTING_KEY = 'system.modules'

export type SystemModuleDef = {
  id: SystemModuleId
  label: string
  description: string
  pathPrefix: string
  accessPermission: string
}

/** Orden de visualización en la consola de administración. */
export const SYSTEM_MODULE_DEFINITIONS: readonly SystemModuleDef[] = [
  { id: 'students', label: 'Estudiantes', description: 'Gestión de estudiantes', pathPrefix: ROUTES.STUDENTS, accessPermission: 'students.access' },
  { id: 'teachers', label: 'Docentes', description: 'Gestión de docentes', pathPrefix: ROUTES.TEACHERS, accessPermission: 'teachers.access' },
  { id: 'tutors', label: 'Tutores', description: 'Tutores/as institucionales y estudiantes acompañados', pathPrefix: ROUTES.TUTORS, accessPermission: 'tutors.access' },
  { id: 'courses', label: 'Cursos', description: 'Oferta y cursos', pathPrefix: ROUTES.COURSES, accessPermission: 'courses.access' },
  { id: 'periods', label: 'Períodos', description: 'Períodos académicos', pathPrefix: ROUTES.PERIODS, accessPermission: 'periods.access' },
  { id: 'enrollments', label: 'Matrículas', description: 'Inscripciones', pathPrefix: ROUTES.ENROLLMENTS, accessPermission: 'enrollments.access' },
  { id: 'grades', label: 'Calificaciones', description: 'Notas y rúbricas', pathPrefix: ROUTES.GRADES, accessPermission: 'grades.access' },
  { id: 'attendance', label: 'Asistencia', description: 'Control de asistencia', pathPrefix: ROUTES.ATTENDANCE, accessPermission: 'attendance.access' },
  { id: 'classrooms', label: 'Aulas', description: 'Aulas y horarios', pathPrefix: ROUTES.CLASSROOMS, accessPermission: 'classrooms.access' },
  {
    id: 'logistics',
    label: 'Logística',
    description: 'Excursiones, movimientos y actividades logísticas',
    pathPrefix: ROUTES.LOGISTICS,
    accessPermission: 'logistics.access',
  },
  { id: 'followUp', label: 'Seguimiento', description: 'Seguimiento y encuestas', pathPrefix: ROUTES.FOLLOW_UP, accessPermission: 'followup.access' },
  { id: 'users', label: 'Usuarios', description: 'Cuentas y roles (administración)', pathPrefix: ROUTES.USERS, accessPermission: 'users.access' },
  { id: 'notifications', label: 'Notificaciones', description: 'Avisos y mensajes', pathPrefix: ROUTES.NOTIFICATIONS, accessPermission: 'notifications.access' },
  {
    id: 'initialSetup',
    label: 'Configuración inicial',
    description: 'Guía de puesta en marcha del establecimiento',
    pathPrefix: ROUTES.INITIAL_SETUP,
    accessPermission: 'initialSetup.access',
  },
  {
    id: 'studyPlans',
    label: 'Planes de estudios',
    description: 'Carreras, materias y correlatividades',
    pathPrefix: ROUTES.STUDY_PLANS,
    accessPermission: 'studyPlans.access',
  },
  {
    id: 'gradeScales',
    label: 'Escalas de notas',
    description: 'Escalas numéricas y asignación a carreras',
    pathPrefix: ROUTES.GRADE_SCALES,
    accessPermission: 'gradeScales.access',
  },
  {
    id: 'actaBooks',
    label: 'Libro de actas',
    description: 'Actas regulares y de promoción',
    pathPrefix: ROUTES.ACTA_BOOKS,
    accessPermission: 'actaBooks.access',
  },
  {
    id: 'elBicho',
    label: 'El bicho',
    description: 'Plan de estudios y verificación de egreso por PDF con IA',
    pathPrefix: ROUTES.EL_BICHO,
    accessPermission: 'elBicho.access',
  },
] as const

export function defaultEnabledModulesMap(): Record<SystemModuleId, boolean> {
  const m = {} as Record<SystemModuleId, boolean>
  for (const def of SYSTEM_MODULE_DEFINITIONS) {
    m[def.id] = true
  }
  return m
}

export function mergeEnabledModules(
  partial: Partial<Record<string, boolean>> | null | undefined
): Record<SystemModuleId, boolean> {
  const base = defaultEnabledModulesMap()
  if (!partial) return base
  for (const def of SYSTEM_MODULE_DEFINITIONS) {
    const v = partial[def.id]
    if (typeof v === 'boolean') base[def.id] = v
  }
  return base
}

export function pathnameToModuleId(pathname: string): SystemModuleId | null {
  const sorted = [...SYSTEM_MODULE_DEFINITIONS].sort((a, b) => b.pathPrefix.length - a.pathPrefix.length)
  for (const def of sorted) {
    if (pathname === def.pathPrefix || pathname.startsWith(`${def.pathPrefix}/`)) {
      return def.id
    }
  }
  return null
}

export function moduleAccessPermission(moduleId: SystemModuleId): string {
  const def = SYSTEM_MODULE_DEFINITIONS.find((m) => m.id === moduleId)
  return def?.accessPermission ?? ''
}
